/**
 * Uint8Array hex / base64 polyfill — the "Uint8Array from base64" proposal.
 *
 * WHY THIS EXISTS, and why it is plain `.js` rather than `.ts`:
 *
 * pdf.js 5.x calls these methods UNGUARDED. The one that matters is in the
 * WORKER: `PDFDocument.get fingerprints` does
 *
 *     [hashOriginal.toHex(), hashModified?.toHex() ?? null]
 *
 * (`pdfjs-dist/build/pdf.worker.mjs`), and `fingerprints` is read on every
 * `getDocument`, so a missing `toHex` fails EVERY PDF before page 1 with
 * `UnknownErrorException: s.toHex is not a function`. The main-thread
 * `pdf.mjs` additionally calls `toBase64()` at two sites.
 *
 * These methods shipped in **Chrome 140 / Firefox 133 / Safari 18.2**. The app's
 * floor is `chrome >= 60` (see .browserslistrc and docs/features/browserSupport.md),
 * so every PDF background was blank on every engine below that — including the
 * classroom panels the floor exists for. Measured on Chrome 137: none of the six
 * methods exist; on Chrome 149: all six do.
 *
 * This is a **runtime API**, not syntax, so transpiling cannot help — and
 * core-js does not supply it either: babel.config.js sets
 * `corejs: { proposals: false }`, and `useBuiltIns: 'usage'` cannot statically
 * see an instance-method call on an unknown receiver anyway.
 *
 * It must be installed in BOTH realms. The failing call is inside the worker,
 * which is a separate bundle with its own global scope — a main-thread
 * polyfill is invisible to it. Hence:
 *   - `src/legacyBrowserPolyfills.ts` imports this (main thread), and
 *   - `webpack.worker.config.js` prepends it to the worker entry.
 *
 * Kept as plain `.js` because the worker config resolves and babel-loads
 * `/\.m?js$/` only — it has no ts-loader and no `.ts` in `resolve.extensions`.
 *
 * Every method is feature-detected, so current engines keep the native
 * implementation and pay only the download.
 *
 * SCOPE — the ENCODE side is spec-exact, the DECODE side is deliberately loose.
 * `toHex`, `toBase64`, `fromHex`, `setFromHex` and `setFromBase64` were
 * differentially tested against Chrome 149's native implementation over 3024
 * cases (every payload length, both alphabets, padded and `omitPadding`, every
 * destination-buffer length) and match 3024/3024. pdf.js only ever calls
 * `toHex` (worker) and `toBase64` (main thread), so the decode paths below are
 * a convenience and knowingly diverge from the proposal in the error cases:
 *
 *   - `fromBase64('A')` returns `[]`; the spec throws (lone trailing character).
 *   - `fromBase64('AA=')` returns `[0]`; the spec throws (malformed padding).
 *   - `fromBase64('AAAA=AAA')` returns `[0,0,0]`; the spec throws (data after
 *     padding — the `break` on `=` silently discards the tail).
 *   - `setFromHex('abc')` returns `{read:2,written:1}`; the spec throws on an
 *     odd-length string.
 *   - `lastChunkHandling` ('strict' / 'stop-before-partial') is ignored.
 *   - Form-feed (\x0c) is not in the skipped-whitespace set, though the
 *     proposal's ASCII-whitespace set includes it.
 *
 * These are latent, not shipped: nothing in the app reaches them. If a future
 * consumer needs spec-faithful decoding, tighten these cases rather than
 * assuming the encode side's rigour extends to them.
 */

/** Define a method non-enumerably, and only when it is missing. */
function define(target, name, value) {
  if (typeof target[name] === 'function') return;
  Object.defineProperty(target, name, {
    value: value,
    writable: true,
    enumerable: false,
    configurable: true,
  });
}

var HEX_DIGITS = '0123456789abcdef';

var BASE64_STANDARD = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
var BASE64_URL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

function alphabetOf(options) {
  var name = options && options.alphabet ? options.alphabet : 'base64';
  if (name === 'base64') return BASE64_STANDARD;
  if (name === 'base64url') return BASE64_URL;
  throw new TypeError('Expected alphabet to be either "base64" or "base64url"');
}

function encodeBase64(bytes, chars, padded) {
  var out = '';
  var i = 0;
  for (; i + 2 < bytes.length; i += 3) {
    var n = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];
    out +=
      chars[(n >> 18) & 63] + chars[(n >> 12) & 63] + chars[(n >> 6) & 63] + chars[n & 63];
  }
  var remaining = bytes.length - i;
  if (remaining === 1) {
    var a = bytes[i] << 16;
    out += chars[(a >> 18) & 63] + chars[(a >> 12) & 63];
    if (padded) out += '==';
  } else if (remaining === 2) {
    var b = (bytes[i] << 16) | (bytes[i + 1] << 8);
    out += chars[(b >> 18) & 63] + chars[(b >> 12) & 63] + chars[(b >> 6) & 63];
    if (padded) out += '=';
  }
  return out;
}

/**
 * Decode base64, optionally bounded by `maxBytes`.
 *
 * The bounded case has non-obvious semantics that a naive bit-accumulator gets
 * wrong (this shim did, until it was differentially tested against Chrome 149's
 * native implementation over 3024 cases). Decoding proceeds in CHUNKS of four
 * base64 characters, each producing three bytes, and a chunk is written only if
 * ALL of its output fits in the remaining space — there is no partial write of
 * a chunk, and `read` does not advance past a chunk that was not written.
 *
 * So for a 5-byte payload ("AAEPEP8="), a 4-byte destination yields
 * `{ read: 4, written: 3 }` — the first chunk only — and a 2-byte destination
 * yields `{ read: 0, written: 0 }`, because even the first chunk's 3 bytes do
 * not fit. `read` counts trailing `=` padding when the tail chunk IS written.
 *
 * @param {string} text
 * @param {string} chars alphabet
 * @param {number|undefined} maxBytes destination capacity, or undefined for "all"
 * @returns {{ bytes: number[], read: number }}
 */
function decodeBase64(text, chars, maxBytes) {
  var bytes = [];
  var read = 0;
  var group = [];
  var unbounded = maxBytes === undefined;

  /** Room for `n` more bytes? */
  function fits(n) {
    return unbounded || bytes.length + n <= maxBytes;
  }

  for (var i = 0; i < text.length; i++) {
    var ch = text.charAt(i);
    if (ch === '=') break;
    if (ch === '\n' || ch === '\r' || ch === '\t' || ch === ' ') continue;

    var value = chars.indexOf(ch);
    if (value === -1) {
      throw new SyntaxError('Invalid character in base64 string: "' + ch + '"');
    }
    group.push(value);

    if (group.length === 4) {
      if (!fits(3)) return { bytes: bytes, read: read };
      var n = (group[0] << 18) | (group[1] << 12) | (group[2] << 6) | group[3];
      bytes.push((n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff);
      read = i + 1;
      group = [];
    }
  }

  // Tail: 2 leftover characters encode 1 byte, 3 encode 2. A single leftover
  // character cannot encode anything and is discarded, as the proposal requires.
  if (group.length >= 2) {
    var produced = group.length === 2 ? 1 : 2;
    if (!fits(produced)) return { bytes: bytes, read: read };
    var tail = (group[0] << 18) | (group[1] << 12) | ((group[2] || 0) << 6);
    bytes.push((tail >> 16) & 0xff);
    if (produced === 2) bytes.push((tail >> 8) & 0xff);
    // The whole remainder of the string, padding included, has been consumed.
    read = text.length;
  }

  return { bytes: bytes, read: read };
}

function assertUint8Array(value, method) {
  if (!(value instanceof Uint8Array)) {
    throw new TypeError('Uint8Array.prototype.' + method + ' called on incompatible receiver');
  }
}

// ---------------------------------------------------------------------------
// Prototype methods
// ---------------------------------------------------------------------------

define(Uint8Array.prototype, 'toHex', function toHex() {
  assertUint8Array(this, 'toHex');
  var out = '';
  for (var i = 0; i < this.length; i++) {
    var byte = this[i];
    out += HEX_DIGITS[(byte >> 4) & 15] + HEX_DIGITS[byte & 15];
  }
  return out;
});

define(Uint8Array.prototype, 'toBase64', function toBase64(options) {
  assertUint8Array(this, 'toBase64');
  var chars = alphabetOf(options);
  // The proposal defaults `omitPadding` to false, i.e. output IS padded.
  var padded = !(options && options.omitPadding);
  return encodeBase64(this, chars, padded);
});

define(Uint8Array.prototype, 'setFromHex', function setFromHex(text) {
  assertUint8Array(this, 'setFromHex');
  if (typeof text !== 'string') throw new TypeError('Expected a string');
  var usable = Math.min(this.length, Math.floor(text.length / 2));
  for (var i = 0; i < usable; i++) {
    var pair = text.substr(i * 2, 2);
    if (!/^[0-9a-fA-F]{2}$/.test(pair)) {
      throw new SyntaxError('Invalid character in hex string: "' + pair + '"');
    }
    this[i] = parseInt(pair, 16);
  }
  return { read: usable * 2, written: usable };
});

define(Uint8Array.prototype, 'setFromBase64', function setFromBase64(text, options) {
  assertUint8Array(this, 'setFromBase64');
  if (typeof text !== 'string') throw new TypeError('Expected a string');
  var result = decodeBase64(text, alphabetOf(options), this.length);
  for (var i = 0; i < result.bytes.length; i++) this[i] = result.bytes[i];
  return { read: result.read, written: result.bytes.length };
});

// ---------------------------------------------------------------------------
// Static constructors
// ---------------------------------------------------------------------------

define(Uint8Array, 'fromHex', function fromHex(text) {
  if (typeof text !== 'string') throw new TypeError('Expected a string');
  if (text.length % 2 !== 0) {
    throw new SyntaxError('Hex string must have an even number of characters');
  }
  var out = new Uint8Array(text.length / 2);
  out.setFromHex(text);
  return out;
});

define(Uint8Array, 'fromBase64', function fromBase64(text, options) {
  if (typeof text !== 'string') throw new TypeError('Expected a string');
  var result = decodeBase64(text, alphabetOf(options), undefined);
  var out = new Uint8Array(result.bytes.length);
  for (var i = 0; i < result.bytes.length; i++) out[i] = result.bytes[i];
  return out;
});

// No `export {}` on purpose: this file is a side-effect SCRIPT, and the app's
// webpack config has no loader rule for `src/**/*.js`, so an ESM marker here
// fails the build with "'import' and 'export' may appear only with
// 'sourceType: module'". It is consumed for its side effects only, from both
// src/legacyBrowserPolyfills.ts and the worker entry.
